import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

// ─── Risk factor data ───────────────────────────────────────────────────────

const RISK_FACTORS = [
  { label: 'Diet', angle: 0 },
  { label: 'Genetics', angle: 60 },
  { label: 'Activity', angle: 120 },
  { label: 'Sleep', angle: 180 },
  { label: 'Stress', angle: 240 },
  { label: 'History', angle: 300 },
];

// Layout constants — everything is relative to the SVG viewBox centre (CX, CY)
const CX = 150;
const CY = 110;
const ORBIT_RADIUS = 68;
const PERSON_R = 28;
const SVG_W = 300;
const SVG_H = 220;

const BUBBLE_RX = 28; // half-width of the pill rect
const BUBBLE_RY = 11; // half-height of the pill rect

function polar(angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + Math.cos(rad) * ORBIT_RADIUS, y: CY + Math.sin(rad) * ORBIT_RADIUS };
}

/** Shorten a line so it starts at the person-circle edge and ends at the bubble-pill edge. */
function clippedEndpoints(angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);

  // Start: offset outward from centre by person circle radius
  const x1 = CX + dx * (PERSON_R + 1);
  const y1 = CY + dy * (PERSON_R + 1);

  // End: offset inward from bubble centre by the pill radius along the line direction.
  // For a rounded-rect pill, the edge distance depends on the angle into the rect.
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  const edgeDist = absDx < 0.001 ? BUBBLE_RY : absDy < 0.001 ? BUBBLE_RX : Math.min(BUBBLE_RX / absDx, BUBBLE_RY / absDy);
  const bx = CX + dx * ORBIT_RADIUS;
  const by = CY + dy * ORBIT_RADIUS;
  const x2 = bx - dx * Math.min(edgeDist, ORBIT_RADIUS * 0.4);
  const y2 = by - dy * Math.min(edgeDist, ORBIT_RADIUS * 0.4);

  return { x1, y1, x2, y2 };
}

// ─── Animation component ────────────────────────────────────────────────────

function PersonalizationAnimation() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="mx-auto w-full max-w-sm rounded-2xl bg-white/5">
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="h-auto w-full" aria-hidden="true">
        {/* Layer 1: Connecting lines — clipped so they don't overlap circle or bubbles */}
        {RISK_FACTORS.map((factor, i) => {
          const ep = clippedEndpoints(factor.angle);
          return (
            <motion.line
              key={`line-${factor.label}`}
              x1={ep.x1}
              y1={ep.y1}
              x2={ep.x2}
              y2={ep.y2}
              stroke="#7CF1C7"
              strokeWidth={0.5}
              initial={{ opacity: 0 }}
              animate={{ opacity: visible ? 0.3 : 0 }}
              transition={{ duration: 0.4, delay: i * 0.12 + 0.4 }}
            />
          );
        })}

        {/* Layer 2: Factor bubbles (cover the line endpoints) */}
        {RISK_FACTORS.map((factor, i) => {
          const { x, y } = polar(factor.angle);
          return (
            <motion.g
              key={factor.label}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: visible ? 1 : 0, scale: visible ? 1 : 0 }}
              transition={{ duration: 0.35, delay: i * 0.12 + 0.4, ease: 'easeOut' }}
              style={{ transformOrigin: `${x}px ${y}px` }}
            >
              <rect
                x={x - 28}
                y={y - 11}
                width={56}
                height={22}
                rx={11}
                fill="rgba(124,241,199,0.12)"
              />
              <text
                x={x}
                y={y + 4}
                textAnchor="middle"
                fill="#7CF1C7"
                fontSize={11}
                fontWeight={500}
                fontFamily="system-ui, -apple-system, sans-serif"
              >
                {factor.label}
              </text>
            </motion.g>
          );
        })}

        {/* Layer 3: Central person circle (covers the line centre) */}
        <motion.circle
          cx={CX}
          cy={CY}
          r={PERSON_R}
          fill="rgba(124,241,199,0.2)"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: visible ? 1 : 0, scale: visible ? 1 : 0.6 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ transformOrigin: `${CX}px ${CY}px` }}
        />

        {/* Layer 4: Person icon (on top) */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: visible ? 1 : 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <circle cx={CX} cy={CY - 6} r={5} fill="#7CF1C7" />
          <path
            d={`M${CX} ${CY + 2}c-5.5 0-10 2.2-10 5v2.5h20v-2.5c0-2.8-4.5-5-10-5z`}
            fill="#7CF1C7"
          />
        </motion.g>
      </svg>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function PersonalizationInterstitial() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-6">
      <motion.div
        className="flex w-full max-w-md flex-col items-center gap-6 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ willChange: 'transform, opacity' }}
      >
        <h3 className="text-2xl font-semibold leading-tight text-white md:text-3xl">
          Search engines and AI tools provide broad guidance based on population data.
        </h3>
        <p className="max-w-sm text-lg leading-relaxed text-white/65">
          Catch combines validated research with your specific history and habits to build a personalized risk profile.
        </p>
        <PersonalizationAnimation />
      </motion.div>
    </div>
  );
}
